import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { FormattedMessage } from "react-intl";
import Loader from "../Loader";

/**
 * The base button (Shared chrome board, turn 8 — the filled-primary rule).
 *
 * Four ranks, all tokens:
 * - primary      the Apply pair — an accent tint in dark, solid accent with ink
 *                text in light. The loudest thing on a screen, so one per screen.
 * - secondary    the accent ring on transparent, accent text.
 * - tertiary     the quiet tint fill — Annuler, Plus tard, the middle of nowhere.
 * - destructive  a flame ring with flame text; the flame tint fills on hover.
 *
 * No colour prop: a consumer that needs a colour is asking for a rank instead.
 * No auto margins: the parent lays the button out with flex and gap.
 */
export type ButtonVariant = "primary" | "secondary" | "tertiary" | "destructive";

export const BUTTON_FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color-mix(in_srgb,var(--s4s-accent)_45%,transparent)]";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-tint-apply-bg text-tint-apply-fg hover:bg-[color-mix(in_srgb,var(--s4s-apply-bg)_88%,var(--s4s-text))]",
  secondary: "ring-1 ring-inset ring-accent text-accent hover:bg-tint-hover active:bg-tint-pressed",
  tertiary: "bg-tint-pill text-text hover:bg-tint-hover active:bg-tint-pressed",
  destructive: "ring-1 ring-inset ring-flame text-flame hover:bg-tint-flame",
};

type LegacyProps = {
  /** Legacy palette name — mapped onto a rank until the call sites move. */
  color?: string;
  /** Legacy — outline maps to secondary. */
  appearance?: "outline" | "full";
  /** Legacy — maps to size="block". */
  fillSpace?: boolean;
};

type Props = LegacyProps & {
  /** i18n id rendered as the label; children render after it. */
  name?: string;
  children?: React.ReactNode;
  icon?: any;
  onClick?: (event?: any) => void;
  variant?: ButtonVariant;
  /** block fills the parent's width; default hugs its content. */
  size?: "default" | "block";
  /** Keeps its width, swaps the label for the small loader, and cannot be clicked. */
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  testId?: string;
  type?: "button" | "submit";
};

/** The colour-name era, translated: outline meant emphasis (now the accent ring),
    primary meant the loud fill, anything muted meant the quiet fill. */
function legacyVariant({ color, appearance }: LegacyProps): ButtonVariant {
  if (appearance === "outline") return "secondary";
  if (color === "primary" || color === undefined) return "primary";
  return "tertiary";
}

function Button({
  name,
  children,
  icon,
  onClick,
  variant,
  size,
  loading,
  disabled,
  className = "",
  testId,
  type = "button",
  color,
  appearance,
  fillSpace,
}: Props) {
  const rank = variant ?? legacyVariant({ color, appearance });
  const block = size === "block" || fillSpace;
  const inert = disabled || loading;

  return (
    <button
      type={type}
      data-testid={testId ?? (name ? `button-${name}` : undefined)}
      aria-disabled={inert}
      disabled={inert}
      onClick={(event) => {
        if (!inert) onClick?.(event);
      }}
      className={
        (block ? "w-full " : "w-fit ") +
        "min-h-[44px] px-4 py-2 rounded-pill text-row font-medium inline-flex items-center justify-center gap-2 text-center " +
        "disabled:opacity-disabled disabled:cursor-not-allowed " +
        VARIANTS[rank] + " " + BUTTON_FOCUS + " " + className
      }
    >
      {loading ? (
        <Loader size="control" />
      ) : (
        <>
          {name && <FormattedMessage id={name} />}
          {children}
          {icon && <FontAwesomeIcon icon={icon} />}
        </>
      )}
    </button>
  );
}

/** The round icon-only button: a tint circle with the text colour's icon, or the
    accent ring when it carries the screen's emphasis. */
export function IconButton({
  icon,
  onClick,
  variant = "neutral",
  label,
  disabled,
  className = "",
  testId,
}: {
  icon: any;
  onClick?: (event?: any) => void;
  variant?: "neutral" | "accent";
  /** i18n id for the sr-only label — an icon button always says what it does. */
  label?: string;
  disabled?: boolean;
  className?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={
        "w-11 h-11 rounded-pill inline-flex items-center justify-center disabled:opacity-disabled " +
        (variant === "accent"
          ? "ring-1 ring-inset ring-accent text-accent hover:bg-tint-hover "
          : "bg-tint-pill text-text hover:bg-tint-hover active:bg-tint-pressed ") +
        BUTTON_FOCUS + " " + className
      }
    >
      <FontAwesomeIcon icon={icon} />
      {label && (
        <span className="sr-only">
          <FormattedMessage id={label} />
        </span>
      )}
    </button>
  );
}

export default Button;
